// Goal-aware weekly targets (docs/training_science.md §2-3, §6).
//
// Every goal belongs to one of five families; the family and the days a week
// you train set how many strength, hard interval and cardio sessions make a
// week "on target". One set of numbers feeds What's next, the Activity card
// and the generator, so they always agree.
//
// Targets add up to at most days + 1: one session a week may count twice
// (strength with an interval finisher), as the What's next shapes already do.

import { weekStart } from "./activitySummary";

export const GOAL_FAMILIES = {
  health: "Health and longevity",
  muscle: "Muscle",
  strength: "Strength and power",
  endurance: "Endurance",
  performance: "Performance",
};

// The goal picker's 19 goals (config/goals.js), by family.
const GOAL_TO_FAMILY = {
  "general fitness": "health",
  "longevity (attia protocol)": "health",
  "stress relief": "health",
  "improve posture": "health",
  "stability & balance": "health",
  "fat loss": "health",
  "build muscle": "muscle",
  "body toning": "muscle",
  "body recomposition": "muscle",
  "get stronger": "strength",
  "power development": "strength",
  "learn a new lift": "strength",
  "improve form/technique": "strength",
  "bodyweight mastery (calisthenics)": "strength",
  "build endurance": "endurance",
  "zone 2 cardio": "endurance",
  "vo2max development": "endurance",
  "athletic performance": "performance",
  "sport-specific training": "performance",
  "work capacity": "performance",
  "metabolic conditioning (metcon)": "performance",
};

// Free text (older programmes stored "strength", "hypertrophy"...) is read
// loosely; anything unrecognised is Health and longevity.
const FAMILY_PATTERNS = [
  ["muscle", /muscle|hypertroph|toning|recomp|size|mass/],
  ["strength", /strong|strength|power|lift|technique|form|calisthen|bodyweight/],
  ["endurance", /endur|zone ?2|vo2|aerobic|cardio|run|marathon|cycl|triathlon/],
  ["performance", /athlet|sport|work capacity|metcon|conditioning|performance/],
];

export const goalFamily = (goal) => {
  const text = String(goal || "").trim().toLowerCase();
  if (!text) return "health";
  if (GOAL_TO_FAMILY[text]) return GOAL_TO_FAMILY[text];
  const hit = FAMILY_PATTERNS.find(([, re]) => re.test(text));
  return hit ? hit[0] : "health";
};

// [resistance, anaerobic, aerobic] by days a week (1, 2, 3, 4, 5+).
const TABLE = {
  health: { 1: [1, 1, 0], 2: [2, 1, 0], 3: [2, 1, 1], 4: [2, 1, 2], 5: [2, 1, 3] },
  muscle: { 1: [1, 0, 1], 2: [2, 0, 0], 3: [3, 0, 1], 4: [4, 0, 1], 5: [4, 0, 2] },
  strength: { 1: [1, 0, 1], 2: [2, 0, 0], 3: [3, 0, 1], 4: [3, 1, 1], 5: [4, 1, 1] },
  endurance: { 1: [1, 0, 1], 2: [1, 0, 2], 3: [1, 1, 2], 4: [2, 1, 2], 5: [2, 1, 3] },
  performance: { 1: [1, 1, 0], 2: [2, 1, 0], 3: [2, 1, 1], 4: [2, 2, 1], 5: [2, 2, 2] },
};

// Hard sets per muscle a week: Galpin's (and ACSM's) ~10 for every goal,
// with more paying off up to ~20 when building muscle is the point.
export const SET_TARGET = 10;
export const SET_CEILING = 20;

// From this age WHO advises strength and balance work on 3+ days a week.
export const OLDER_ADULT_AGE = 65;

const ageFrom = (userProfile, now) => {
  const year = Number(userProfile?.birthYear);
  if (!Number.isInteger(year) || year < 1900) return null;
  return new Date(now).getFullYear() - year;
};

const clampDays = (n) => Math.min(Math.max(Math.round(n), 1), 7);

/**
 * Typical training days a week from history: the median across the weeks
 * you trained in, out of the last 8 complete calendar weeks, once at least 4
 * of them had any training. Weeks off (a holiday, illness) don't drag the
 * figure down. Null when there isn't enough to go on.
 */
export const inferWeeklyDays = (dates, { now = Date.now(), weeks = 8 } = {}) => {
  const thisWeek = weekStart(now).getTime();
  const DAY_MS = 24 * 60 * 60 * 1000;
  const perWeek = new Map();
  const seen = new Set();
  for (const value of Array.isArray(dates) ? dates : []) {
    const d =
      typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
        ? new Date(...value.split("-").map((n, i) => Number(n) - (i === 1 ? 1 : 0)))
        : new Date(value);
    if (Number.isNaN(d.getTime())) continue;
    const day = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    if (seen.has(day)) continue;
    seen.add(day);
    const key = weekStart(d).getTime();
    if (key >= thisWeek) continue; // only complete weeks
    if (key < thisWeek - weeks * 7 * DAY_MS) continue;
    perWeek.set(key, (perWeek.get(key) || 0) + 1);
  }
  if (perWeek.size < Math.ceil(weeks / 2)) return null;
  const counts = [...perWeek.values()].sort((a, b) => a - b);
  const mid = counts.length / 2;
  const median =
    counts.length % 2 ? counts[Math.floor(mid)] : (counts[mid - 1] + counts[mid]) / 2;
  return median >= 1 ? clampDays(median) : null;
};

/**
 * Days a week, and where it came from: an active programme, the Profile,
 * the last 8 weeks of history, or the default of 3.
 */
export const resolveWeeklyDays = ({ programme, userProfile, trainingDates, now = Date.now() } = {}) => {
  const fromProgramme = Number(programme?.days_per_week);
  if (Number.isInteger(fromProgramme) && fromProgramme > 0) {
    return { days: fromProgramme, source: "programme" };
  }
  const fromProfile = Number(userProfile?.weeklySessions);
  if (Number.isInteger(fromProfile) && fromProfile > 0) {
    return { days: fromProfile, source: "profile" };
  }
  const inferred = inferWeeklyDays(trainingDates, { now });
  if (inferred) return { days: inferred, source: "history" };
  return { days: 3, source: "default" };
};

/**
 * The weekly targets for this person right now.
 *
 * @returns {{ family, familyLabel, days, daysSource, counts: {resistance, anaerobic, aerobic},
 *            setTarget, setCeiling, olderAdultSuggestion: boolean }}
 */
export const computeWeeklyTargets = ({
  programme,
  userProfile,
  trainingDates,
  now = Date.now(),
} = {}) => {
  const { days, source } = resolveWeeklyDays({ programme, userProfile, trainingDates, now });
  const family = goalFamily(programme?.focus || userProfile?.primaryGoal);
  const [resistance, anaerobic, aerobic] = TABLE[family][Math.min(days, 5)];
  const counts = { resistance, anaerobic, aerobic };

  // Overrides the user accepted (e.g. the over-65 strength suggestion).
  const overrides = userProfile?.weeklyTargetOverrides || {};
  for (const tag of Object.keys(counts)) {
    const n = Number(overrides[tag]);
    if (Number.isInteger(n) && n >= 0) counts[tag] = Math.max(counts[tag], n);
  }

  const age = ageFrom(userProfile, now);
  const olderAdultSuggestion =
    age != null &&
    age >= OLDER_ADULT_AGE &&
    days >= 3 &&
    counts.resistance < 3;

  return {
    family,
    familyLabel: GOAL_FAMILIES[family],
    days,
    daysSource: source,
    counts,
    setTarget: SET_TARGET,
    setCeiling: SET_CEILING,
    olderAdultSuggestion,
  };
};

/** "2 strength sessions, 1 hard interval session and 1 cardio session". */
export const describeTargets = (counts) => {
  const part = (n, name) => n > 0 && `${n} ${name} ${n === 1 ? "session" : "sessions"}`;
  const parts = [
    part(counts.resistance, "strength"),
    part(counts.anaerobic, "hard interval"),
    part(counts.aerobic, "cardio"),
  ].filter(Boolean);
  return parts.length > 1
    ? `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`
    : parts[0] || "";
};
